import ProductValueLabel from '@/components/web/components/PhysicalProductSelection/ProductValueLabel';
import { normalizePhysicalSamplingConfig } from '@/components/web/components/PhysicalProductSelection/adapters';
import { SamplingMode } from '@/components/web/components/PhysicalProductSelection/types';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CreatorQuotePreviewResponse } from '@/interface/creatorOrderV2';
import CreatorPackageBonusBanner from './CreatorPackageBonusBanner';
import CreatorPriceLoadingRegion from './CreatorPriceLoadingRegion';

const formatUsd = (value: number) =>
  `${new Intl.NumberFormat('en-US', {
    maximumFractionDigits: 2
  }).format(Math.max(0, value))} USD`;

export type CreatorPackagePricingDetailsProps = {
  placementTotal: number;
  packageRate: number;
  /** Non-cash product benefit, displayed once without changing quote totals. */
  freeProduct?: { value?: number; product?: unknown };
  singleChannelSummary?: {
    label: string;
    description?: string;
  };
  bonusAmount?: number;
  showActions?: boolean;
  submitLabel?: string;
  submitting?: boolean;
  submitDisabled?: boolean;
  recommendationLoading?: boolean;
  onSubmit?: () => void;
  onCancel?: () => void;
  className?: string;
  previewPricing?: boolean;
  pricingPreview?: CreatorQuotePreviewResponse | null;
  pricingLoading?: boolean;
  pricingError?: boolean;
  onRetryPricing?: () => void;
  showPackageDiscount?: boolean;
  /** Existing quote requirements summary, between price totals and bonus/actions. */
  requirementsSummary?: ReactNode;
};

export default function CreatorPackagePricingDetails({
  placementTotal,
  packageRate,
  freeProduct,
  singleChannelSummary,
  bonusAmount = 0,
  showActions = false,
  submitLabel,
  submitting = false,
  submitDisabled = false,
  recommendationLoading = false,
  onSubmit,
  onCancel,
  className = '',
  previewPricing = false,
  pricingPreview,
  pricingLoading = false,
  pricingError = false,
  onRetryPricing,
  showPackageDiscount = true,
  requirementsSummary
}: CreatorPackagePricingDetailsProps) {
  const { t } = useTranslation();
  const displayedPlacementTotal = pricingPreview?.listTotal ?? placementTotal;
  const packageDiscount =
    pricingPreview?.discountAmount ?? Math.max(0, placementTotal - packageRate);
  const discountPercentage = Math.round(
    (pricingPreview?.discountRate ??
      (placementTotal > 0 ? packageDiscount / placementTotal : 0)) * 100
  );
  const serviceFee = pricingPreview
    ? pricingPreview.serviceFee
    : previewPricing
      ? Math.round(packageRate * 0.05)
      : undefined;
  const serviceFeePercent = pricingPreview
    ? new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(
        pricingPreview.serviceFeeRate * 100
      )
    : undefined;
  const originalServiceFee = pricingPreview
    ? pricingPreview.subsidyAmount
    : previewPricing
      ? Math.round(placementTotal * 0.1)
      : undefined;
  const showOriginalServiceFee =
    serviceFee !== undefined &&
    originalServiceFee !== undefined &&
    originalServiceFee !== serviceFee;
  const creatorReceives =
    pricingPreview?.amount ?? Math.max(0, packageRate - (serviceFee ?? 0));
  const showCashReceipt = !freeProduct || creatorReceives !== 0;
  const multiple =
    normalizePhysicalSamplingConfig(freeProduct?.product)?.mode ===
    SamplingMode.MULTIPLE_PRODUCTS;
  const productValue = freeProduct?.value;
  const hasProductValue =
    !multiple &&
    typeof productValue === 'number' &&
    Number.isFinite(productValue) &&
    productValue >= 0;

  return (
    <section className={`w-full ${className}`}>
      <h3 className="m-0 text-base font-semibold leading-6 text-[#292524]">
        {t('multi_platform.quote.pricing_details', {
          defaultValue: 'Pricing details'
        })}
      </h3>

      <CreatorPriceLoadingRegion
        loading={recommendationLoading || pricingLoading}
        showText={false}
        className="mt-4 rounded-xl bg-[#F5F5F4]"
      >
        {singleChannelSummary ? (
          <div
            data-testid="single-channel-pricing-summary"
            className="px-4 pt-3"
          >
            <div className="text-base font-semibold leading-6 text-[#0D0D0D]">
              {singleChannelSummary.label}
            </div>
            {singleChannelSummary.description ? (
              <div className="mt-1 text-sm leading-5 text-[#737373]">
                {singleChannelSummary.description}
              </div>
            ) : null}
          </div>
        ) : null}
        <div
          data-testid="pricing-details-card"
          className={`relative flex flex-col gap-3 overflow-hidden bg-[#F5F5F4] px-4 py-3 text-xs leading-[18px] tracking-[-0.12px] text-[#171717] ${singleChannelSummary ? 'rounded-b-xl' : 'rounded-xl'} ${freeProduct ? (showPackageDiscount ? 'min-h-[158px]' : 'min-h-[124px]') : showPackageDiscount ? 'h-[158px]' : 'h-[124px]'}`}
        >
          <div className="flex h-5 flex-none items-center justify-between gap-4">
            <span>
              {singleChannelSummary ? (
                <span key="deal-price">
                  {t('timeline.content_deal_price', {
                    defaultValue: 'Deal price'
                  })}
                </span>
              ) : (
                <span key="placement-total">
                  {t('multi_platform.quote.placement_total', {
                    defaultValue: 'Placement total'
                  })}
                </span>
              )}
            </span>
            <span className="whitespace-nowrap text-sm leading-5 tracking-normal text-[#525252]">
              {formatUsd(displayedPlacementTotal)}
            </span>
          </div>

          {showPackageDiscount ? (
            <div
              data-testid="package-discount-row"
              className="flex h-[22px] flex-none items-center justify-between gap-4"
            >
              <span>
                {t('multi_platform.quote.package_discount', {
                  defaultValue: 'Package discount'
                })}
              </span>
              <span className="flex items-center gap-2 whitespace-nowrap text-sm leading-5 tracking-normal text-[#525252]">
                <span className="inline-flex h-[22px] items-center rounded-full border border-[#FED7AA] bg-[#FFF7ED] px-2 text-xs font-medium leading-[18px] text-[#C2410C]">
                  {`${discountPercentage}% off`}
                </span>
                <span>-{formatUsd(packageDiscount)}</span>
              </span>
            </div>
          ) : null}

          <div className="flex h-5 flex-none items-center justify-between gap-4 text-[#404040]">
            <span>
              {pricingPreview && serviceFeePercent !== undefined
                ? t('multi_platform.offer.service_fee_rate', {
                    defaultValue: 'Service fee ({percent}%)'
                  }).replace('{percent}', serviceFeePercent)
                : previewPricing
                  ? t('multi_platform.quote.service_fee_percent', {
                      defaultValue: 'Service fee (5%)'
                    })
                  : t('multi_platform.service_fee', {
                      defaultValue: 'Service fee'
                    })}
            </span>
            <span className="flex items-center gap-2 whitespace-nowrap text-sm leading-5 tracking-normal text-[#525252]">
              <span>
                {serviceFee === undefined ? '—' : `-${formatUsd(serviceFee)}`}
              </span>
              {showOriginalServiceFee ? (
                <span className="text-[#A3A3A3] line-through">
                  -{formatUsd(originalServiceFee)}
                </span>
              ) : null}
            </span>
          </div>

          <div className="h-0 w-full flex-none border-t border-dashed border-[#D6D3D1]" />

          <div
            className={`flex flex-none items-center justify-between gap-4 ${freeProduct ? 'min-h-6' : 'h-6'}`}
          >
            <span className="flex-shrink-0">
              {t('multi_platform.you_receive', {
                defaultValue: "You'll receive"
              })}
            </span>
            <strong className="min-w-0 text-right text-base font-semibold leading-6 tracking-[-0.16px] text-[#0D0D0D]">
              {showCashReceipt ? (
                <span className="whitespace-nowrap">
                  {formatUsd(creatorReceives)}
                </span>
              ) : null}
              {freeProduct ? (
                <span>
                  {showCashReceipt ? <span>{' + '}</span> : null}
                  <span>
                    {t('price_details.free_product', {
                      defaultValue: 'Free product'
                    })}
                  </span>
                  {multiple ? (
                    <span
                      key="multiple-product-value"
                      className="mt-1 block text-xs font-normal leading-5 text-[#525252]"
                    >
                      {' ('}
                      <ProductValueLabel product={freeProduct?.product} />
                      {')'}
                    </span>
                  ) : hasProductValue ? (
                    <span
                      key={`single-product-value-${productValue}`}
                      className="whitespace-nowrap text-xs font-normal text-[#525252]"
                    >
                      {` (${formatUsd(productValue)})`}
                    </span>
                  ) : null}
                </span>
              ) : null}
            </strong>
          </div>

          <span
            aria-hidden
            className={`absolute left-[-6px] h-3 w-3 rounded-full bg-white ${showPackageDiscount ? 'top-[104px]' : 'top-[70px]'}`}
          />
          <span
            aria-hidden
            className={`absolute right-[-6px] h-3 w-3 rounded-full bg-white ${showPackageDiscount ? 'top-[104px]' : 'top-[70px]'}`}
          />
        </div>
      </CreatorPriceLoadingRegion>
      {pricingError ? (
        <div
          className="mt-2 flex items-center justify-between gap-2 text-xs text-[#B91C1C]"
          role="alert"
        >
          <span>
            {t('multi_platform.quote.pricing_preview_failed', {
              defaultValue: 'Could not calculate pricing details.'
            })}
          </span>
          {onRetryPricing ? (
            <button
              type="button"
              className="underline"
              onClick={onRetryPricing}
            >
              {t('general_text.retry', { defaultValue: 'Retry' })}
            </button>
          ) : null}
        </div>
      ) : null}

      {requirementsSummary ? (
        <div className="mt-4">{requirementsSummary}</div>
      ) : null}

      <CreatorPackageBonusBanner amount={bonusAmount} className="mt-3" />

      {showActions ? (
        <div className="mt-3 flex flex-col gap-3">
          <button
            type="button"
            disabled={
              submitDisabled ||
              submitting ||
              recommendationLoading ||
              pricingLoading ||
              pricingError
            }
            className="flex h-10 w-full items-center justify-center rounded-md border-0 bg-[#FF5A1F] px-4 text-sm font-semibold text-white transition-colors hover:bg-[#E94F16] disabled:cursor-not-allowed disabled:bg-[#D7D3D0]"
            onClick={onSubmit}
          >
            {submitting
              ? t('multi_platform.quote.submitting', {
                  defaultValue: 'Submitting...'
                })
              : submitLabel ||
                t('multi_platform.quote.share_package_rate', {
                  defaultValue: 'Share package rate'
                })}
          </button>
          <button
            type="button"
            className="flex h-10 w-full items-center justify-center rounded-md border border-[#D6D3D1] bg-white px-4 text-sm font-semibold text-[#44403C] hover:bg-[#FAFAF9]"
            onClick={onCancel}
          >
            {t('general_text.cancel', { defaultValue: 'Cancel' })}
          </button>
        </div>
      ) : null}
    </section>
  );
}
